import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Search, Plus, Grid3x3, List, Shield, ChevronDown, Compass } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardMacroCard } from "@/components/dashboard/DashboardMacroCard";
import { AdminReviewQueue } from "@/components/dashboard/AdminReviewQueue";
import { AdminBulkUpload } from "@/components/dashboard/AdminBulkUpload";
import { AdminReports } from "@/components/dashboard/AdminReports";
import { AdminMacroRequests } from "@/components/dashboard/AdminMacroRequests";

type ViewMode = "grid" | "list";
type FilterTab = "all" | "drafts" | "published" | "favorites" | "admin-review" | "admin-reports" | "admin-bulk-upload" | "admin-requests";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useAdminCheck();
  
  const [user, setUser] = useState<any>(null);
  const [macros, setMacros] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [savedMacros, setSavedMacros] = useState<any[]>([]);
  const [pendingMacros, setPendingMacros] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [tags, setTags] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
        fetchMacros(session.user.id);
        fetchDrafts(session.user.id);
        fetchSavedMacros(session.user.id);
        fetchTags();
      }
    });
  }, [navigate]);

  useEffect(() => {
    if (isAdmin && user) {
      fetchPendingMacros();
    }
  }, [isAdmin, user]);

  const fetchMacros = async (userId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("macros")
        .select(`
          *,
          profiles!macros_owner_id_fkey (username, full_name, verified, avatar_url),
          macro_tags (tags (name))
        `)
        .eq("owner_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Transform tags data structure and rename profiles
      const processedMacros = data?.map(macro => ({
        ...macro,
        public_profiles: macro.profiles,
        tags: macro.macro_tags?.map((mt: any) => mt.tags.name) || []
      })) || [];
      
      setMacros(processedMacros);
    } catch (error) {
      console.error("Error fetching macros:", error);
      toast({
        title: "Error",
        description: "Failed to load your macros",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("macro_drafts")
        .select("*")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setDrafts(data || []);
    } catch (error) {
      console.error("Error fetching drafts:", error);
    }
  };

  const fetchSavedMacros = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("saved_macros")
        .select(`
          macro_id,
          macros (
            id,
            slug,
            name,
            short_desc,
            risk_score,
            views_count,
            screenshot_url,
            is_published,
            is_approved,
            created_at,
            code_text,
            profiles!macros_owner_id_fkey (username, full_name, verified, avatar_url),
            macro_tags (tags (name))
          )
        `)
        .eq("user_id", userId);

      if (error) throw error;
      
      // Extract and flatten the macro data with transformed tags and renamed profiles
      const saved = data?.map(item => ({
        ...(item.macros as any),
        public_profiles: (item.macros as any)?.profiles,
        tags: item.macros?.macro_tags?.map((mt: any) => mt.tags.name) || [],
        type: "saved"
      })) || [];
      
      setSavedMacros(saved);
    } catch (error) {
      console.error("Error fetching saved macros:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .order("name");
      
      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchPendingMacros = async () => {
    try {
      const { data, error } = await supabase
        .from("macros")
        .select(`
          *,
          profiles!macros_owner_id_fkey (username, full_name, verified, avatar_url)
        `)
        .eq("is_approved", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Rename profiles to public_profiles for consistency
      const processedPending = data?.map(macro => ({
        ...macro,
        public_profiles: macro.profiles
      })) || [];
      
      setPendingMacros(processedPending);
    } catch (error) {
      console.error("Error fetching pending macros:", error);
    }
  };

  const handleDelete = async (macroId: string) => {
    if (!confirm("Are you sure you want to delete this macro?")) return;

    try {
      const { error } = await supabase.from("macros").delete().eq("id", macroId);
      if (error) throw error;
      
      toast({ title: "Success", description: "Macro deleted successfully" });
      if (user) fetchMacros(user.id);
    } catch (error) {
      console.error("Error deleting macro:", error);
      toast({
        title: "Error",
        description: "Failed to delete macro",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    if (!confirm("Are you sure you want to delete this draft?")) return;

    try {
      const { error } = await supabase.from("macro_drafts").delete().eq("id", draftId);
      if (error) throw error;
      
      toast({ title: "Success", description: "Draft deleted successfully" });
      if (user) fetchDrafts(user.id);
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast({
        title: "Error",
        description: "Failed to delete draft",
        variant: "destructive",
      });
    }
  };

  const handleUnsaveMacro = async (macroId: string) => {
    if (!confirm("Remove this macro from saved?")) return;

    try {
      const { error } = await supabase
        .from("saved_macros")
        .delete()
        .eq("user_id", user?.id)
        .eq("macro_id", macroId);
      
      if (error) throw error;
      
      toast({ title: "Success", description: "Removed from saved" });
      if (user) fetchSavedMacros(user.id);
    } catch (error) {
      console.error("Error unsaving macro:", error);
      toast({
        title: "Error",
        description: "Failed to unsave macro",
        variant: "destructive",
      });
    }
  };

  const handleCopyCode = async (macroId: string) => {
    // Check if it's a draft or a macro
    const draft = drafts.find(d => d.id === macroId);
    const macro = macros.find(m => m.id === macroId);
    
    let codeText = null;
    
    if (draft) {
      codeText = draft.code_text;
    } else if (macro?.code_text) {
      codeText = macro.code_text;
    }
    
    if (!codeText) {
      toast({ title: "Error", description: "No code found", variant: "destructive" });
      return;
    }

    try {
      await navigator.clipboard.writeText(codeText);
      toast({ title: "Success", description: "Code copied to clipboard" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to copy code", variant: "destructive" });
    }
  };

  // Filter items based on active tab
  const getFilteredItems = () => {
    const allItems = [
      ...drafts.map(d => ({ ...d, type: "draft" })),
      ...macros.map(m => ({ ...m, type: "macro" }))
    ];

    let filtered = allItems;

    switch (activeTab) {
      case "drafts":
        filtered = allItems.filter(item => item.type === "draft");
        break;
      case "published":
        filtered = allItems.filter(item => item.type === "macro" && item.is_published && item.is_approved);
        break;
      case "favorites":
        filtered = savedMacros;
        break;
      case "all":
      default:
        break;
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        (item.name || item.title || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item => 
        item.tags?.includes(selectedCategory)
      );
    }

    return filtered;
  };

  const filteredItems = getFilteredItems();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - MacroVault</title>
        <meta name="description" content="Manage your VBA macros, drafts, and saved macros in your MacroVault dashboard." />
        <link rel="canonical" href="https://findmacros.com/dashboard" />
      </Helmet>
      <Header />
      
      <div className="flex w-full px-6 md:px-8 lg:px-12 pt-24 sm:pt-28 lg:pt-32 relative">
        {/* Sidebar - hidden on mobile */}
        <DashboardSidebar 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          draftsCount={drafts.length}
          publishedCount={macros.filter(m => m.is_published && m.is_approved).length}
          savedCount={savedMacros.length}
          isAdmin={isAdmin}
          pendingCount={pendingMacros.length}
        />

        {/* Main Content */}
        <main className="flex-1 min-w-0 md:ml-64 flex flex-col h-[calc(100vh-6rem)] sm:h-[calc(100vh-7rem)] lg:h-[calc(100vh-8rem)]">
          {/* Top Bar - Fixed */}
          <div className="flex-shrink-0 bg-background py-6 border-b border-border/50">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Search your macros..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-base rounded-full border-0 bg-muted focus-visible:ring-0"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {/* View Toggle */}
                <div className="hidden md:flex gap-1">
                  <Button
                    variant={viewMode === "grid" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("grid")}
                    className="h-10 w-10 p-0 rounded-full"
                  >
                    <Grid3x3 className="h-5 w-5" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className="h-10 w-10 p-0 rounded-full"
                  >
                    <List className="h-5 w-5" />
                  </Button>
                </div>

                {/* Category Filter - Desktop */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="hidden md:flex h-12 rounded-full px-6 text-base">
                      {selectedCategory || "Filter"}
                      <ChevronDown className="ml-2 h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-card z-50">
                    <DropdownMenuItem
                      onClick={() => setSelectedCategory(null)}
                      className={!selectedCategory ? "bg-accent" : ""}
                    >
                      All Categories
                    </DropdownMenuItem>
                    {tags.map(tag => (
                      <DropdownMenuItem
                        key={tag.id}
                        onClick={() => setSelectedCategory(tag.name)}
                        className={selectedCategory === tag.name ? "bg-accent" : ""}
                      >
                        {tag.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Mobile Filters */}
            <div className="md:hidden mb-6 flex gap-2">
              {/* Tab Filter - Mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="whitespace-nowrap h-12 rounded-full px-6 text-base">
                    {activeTab === "all" ? "All" : activeTab === "drafts" ? "Drafts" : activeTab === "favorites" ? "Saved" : "Published"}
                    <ChevronDown className="ml-2 h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-40 bg-card z-50">
                  <DropdownMenuItem
                    onClick={() => setActiveTab("all")}
                    className={activeTab === "all" ? "bg-accent" : ""}
                  >
                    All
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setActiveTab("drafts")}
                    className={activeTab === "drafts" ? "bg-accent" : ""}
                  >
                    Drafts
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setActiveTab("published")}
                    className={activeTab === "published" ? "bg-accent" : ""}
                  >
                    Published
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setActiveTab("favorites")}
                    className={activeTab === "favorites" ? "bg-accent" : ""}
                  >
                    Saved
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Category Filter - Mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="whitespace-nowrap h-12 rounded-full px-6 text-base">
                    {selectedCategory || "Filter"}
                    <ChevronDown className="ml-2 h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card z-50">
                  <DropdownMenuItem
                    onClick={() => setSelectedCategory(null)}
                    className={!selectedCategory ? "bg-accent" : ""}
                  >
                    All Categories
                  </DropdownMenuItem>
                  {tags.map(tag => (
                    <DropdownMenuItem
                      key={tag.id}
                      onClick={() => setSelectedCategory(tag.name)}
                      className={selectedCategory === tag.name ? "bg-accent" : ""}
                    >
                      {tag.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-auto py-6">
            {/* Admin Review Queue */}
            {isAdmin && activeTab === "admin-review" && (
              <AdminReviewQueue 
                pendingMacros={pendingMacros} 
                onRefresh={fetchPendingMacros}
              />
            )}

            {/* Admin Reports */}
            {isAdmin && activeTab === "admin-reports" && (
              <AdminReports />
            )}

            {/* Admin Bulk Upload */}
            {isAdmin && activeTab === "admin-bulk-upload" && (
              <AdminBulkUpload />
            )}

            {/* Admin Macro Requests */}
            {isAdmin && activeTab === "admin-requests" && (
              <AdminMacroRequests />
            )}

            {/* Macros Grid/List - Hide when on admin tabs */}
            {activeTab !== "admin-review" && activeTab !== "admin-reports" && activeTab !== "admin-bulk-upload" && activeTab !== "admin-requests" && (
              <>
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading your macros...</p>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? "No macros found" : activeTab === "favorites" ? "No saved macros yet" : "No macros yet"}
                </p>
                <Button asChild>
                  <Link to={activeTab === "favorites" ? "/vault" : "/upload"}>
                    {activeTab === "favorites" ? (
                      <Compass className="h-4 w-4 mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    {activeTab === "favorites" ? "Explore Macros" : "Create Your First Macro"}
                  </Link>
                </Button>
              </div>
            ) : (
              <div className={
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  : "space-y-2"
              }>
                {filteredItems.map((item) => (
                  <DashboardMacroCard
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    onDelete={
                      item.type === "draft" 
                        ? handleDeleteDraft 
                        : item.type === "saved"
                        ? handleUnsaveMacro
                        : handleDelete
                    }
                    onCopyCode={handleCopyCode}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
